import { defineStore } from "pinia";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { useProjectsStore } from "@/store/modules/projects";
import { useInstallationsStore } from "@/store/modules/installations";
import { useSamplesStore } from "@/store/modules/samples";
import { useRecordBooksStore } from "@/store/modules/recordBooks";

const {
  innerFetch,
  innerCreate,
  innerPartialUpdate,
  innerBulkDelete,
  innerDelete,
} = crudGenerator(db.models.Phase);

export const usePhasesStore = defineStore("phases", {
  state: () => ({
    phaseList: [],
    phaseIsLoading: false,
  }),
  actions: {
    async fetchPhases(projectUuid) {
      return innerFetch(
        { project: projectUuid, pageSize: 500 },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.phaseIsLoading = isLoading;
        }
      );
    },

    async filterPhasesByInstallation(installationUuid) {
      while (this.phaseIsLoading) await new Promise((r) => setTimeout(r, 200));

      this.phaseIsLoading = true;

      try {
        const phases = await db.phases
          .where({ installation: installationUuid })
          .sortBy("order");

        for (const phase of phases) {
          await phase.setRelationships();
        }

        this.phaseList = phases;

        return phases;
      } finally {
        this.phaseIsLoading = false;
      }
    },

    async createPhase({ form, buildSampleChain = true }) {
      const phase = await innerCreate(form, useProjectsStore().isOfflineState, {
        requiresNetwork: true,
      });
      if (buildSampleChain) {
        await useSamplesStore().buildSampleChain(phase.uuid);
      }
      await useInstallationsStore().refreshLastUpdated();
      this.phaseList.push(phase);
      return phase;
    },

    async updatePhase(form) {
      const phase = await innerPartialUpdate(
        form,
        useProjectsStore().isOfflineState
      );
      await this.refreshPhase(phase);
      return phase;
    },

    async refreshPhase(phase) {
      await useInstallationsStore().refreshLastUpdated();
      const index = this.phaseList.findIndex(({ uuid }) => phase.uuid === uuid);
      if (index >= 0) {
        this.phaseList[index] = phase;
        this.phaseList = [...this.phaseList];
      }
    },

    async removePhase(phase) {
      const projectsStore = useProjectsStore();
      await innerDelete(
        phase.uuid,
        projectsStore.isOfflineState,
        projectsStore.currentProjectUuid
      );
      await useRecordBooksStore().archiveRecordBooksByPhases({
        phaseUuids: phase.uuid,
        detachPhase: true,
      });
      await useInstallationsStore().refreshLastUpdated();
      this.phaseList = this.phaseList.filter(({ uuid }) => uuid !== phase.uuid);
    },

    async bulkDeletePhases(uuids) {
      const projectsStore = useProjectsStore();
      await innerBulkDelete(
        uuids,
        projectsStore.isOfflineState,
        projectsStore.currentProjectUuid
      );
      await useRecordBooksStore().archiveRecordBooksByPhases({
        // innerBulkDelete accepte aussi la forme { uuids }
        phaseUuids: Array.isArray(uuids) ? uuids : uuids.uuids,
        detachPhase: true,
      });
      await useInstallationsStore().refreshLastUpdated();
      this.phaseList = [];
    },

    async retrievePhaseByUuid(phaseUuid) {
      return db.phases.get(phaseUuid);
    },
  },
});
